<template>
  <hic-index-page title="排班管理" header-height="4rem" :hide-footer="false">
    <!-- 左侧标题区：班次类型筛选 -->
    <template slot="title">
      <el-row style="width: 40rem">
        <el-tabs v-model="condition.title" @tab-click="loadData">
          <el-tab-pane v-for="t in staffTypes" :key="t.code" :label="t.name" :name="t.code"></el-tab-pane>
        </el-tabs>
      </el-row>
    </template>

    <!-- 右侧搜索区：月份选择器 & 医院筛选 -->
    <template slot="search">
      <el-select v-model="condition.orgName" size="small" clearable placeholder="全部医院" style="width:110px"
        @change="loadData">
        <el-option v-for="h in hospitals" :key="h.code" :label="h.name" :value="h.code" />
      </el-select>
      <el-date-picker v-model="condition.month" type="month" size="small" placeholder="选择月份"
        style="width:140px; margin-left:8px" format="yyyy年MM月" value-format="yyyy-MM" @change="loadData" />
      <el-dropdown split-button type="primary" class="left-pad" size="small" @click="exportExcel"
        @command="handleCommand">
        <i class="el-icon-tickets"></i>
        <span>排班表</span>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item icon="el-icon-money" command="exportSubsidy">补贴汇总</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </template>
    <template slot="footer">
      <hic-paging :entities="entities" :condition="condition" :sizes="[50, 100, 200]" @load="loadData" />
    </template>

    <!-- 班次图例 -->
    <div class="legend-bar">
      <span v-for="s in shiftTypes" :key="s.id" class="legend-item">
        <span class="legend-dot" :style="{ background: s.color }"></span>{{ s.name }}
      </span>
    </div>

    <!-- 排班表主体 -->
    <div class="schedule-wrap" v-loading="loading">
      <table class="schedule-table" v-if="displayStaff.length">
        <!-- 表头：固定列 + 日期列 -->
        <thead>
          <tr class="header-row">
            <th class="sticky-col col-drag"></th>
            <th class="sticky-col col-hospital">医院</th>
            <th class="sticky-col col-name">姓名</th>
            <th class="sticky-col col-phone">电话</th>
            <th v-for="d in dates" :key="d.date" class="date-col" :class="{ weekend: d.isWeekend }">
              <div>{{ d.day }}</div>
              <div class="weekday">{{ d.weekday }}</div>
            </th>
            <th class="sticky-col col-total">工作量合计</th>
          </tr>
        </thead>

        <!-- 表体：支持拖拽行排序 -->
        <draggable v-model="orderedStaff" tag="tbody" handle=".drag-handle" @end="onDragEnd">
          <tr v-for="staff in displayStaff" :key="staff.id" class="staff-row">
            <!-- 拖拽手柄列 -->
            <td class="sticky-col col-drag">
              <i class="el-icon-rank drag-handle" title="拖动排序"></i>
            </td>

            <!-- 医院列（每行显示） -->
            <td class="sticky-col col-hospital">
              {{ staff.orgName | dictText("StaffOrg") }}
            </td>

            <!-- 姓名 -->
            <td class="sticky-col col-name">{{ staff.name }}</td>

            <!-- 电话 -->
            <td class="sticky-col col-phone">{{ staff.phone }}</td>

            <!-- 班次单元格 -->
            <td v-for="d in dates" :key="d.date" class="shift-cell" :title="getShiftName(staff.id, d.date)"
              :style="getCellStyle(staff.id, d.date)" @click="onCellClick(staff, d.date)">
              {{ getShiftName(staff.id, d.date) }}
            </td>

            <!-- 工作量合计列 -->
            <td class="sticky-col col-total">
              {{ getStaffWorkload(staff.id) }}
            </td>
          </tr>
        </draggable>
      </table>

      <el-empty v-else description="暂无数据" :image-size="80" />
    </div>
    <shift-assign :visible.sync="showAssignDialog" :staff-name="currentStaff ? currentStaff.name : ''"
      :staff-id="currentStaff ? currentStaff.id : null"
      :staff-type-code="currentStaff ? currentStaff.title : null" :date="currentDate"
      :current-shift-type-id="currentShiftTypeId" :shift-types="shiftTypes" :series-list="seriesList"
      @ok="handleAssignOk" />
  </hic-index-page>
</template>

<script>
import api from '@/common/api'
import ShiftAssign from './shiftAssign'
import draggable from 'vuedraggable'
import { mapGetters } from "sw-hic";
import ExcelJS from 'exceljs'

export default {
  name: 'scheduleView',
  components: {
    ShiftAssign,
    draggable
  },

  data() {
    const now = new Date()
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    return {
      loading: false,
      exporting: false,
      condition: {
        title: '',
        orgName: null,
        deleted: false,
        month,
        pageIndexOnView: 1,
        pageSize: 50
      },
      entities: {},
      hospitals: [],
      staffTypes: [],
      staffList: [],
      orderedStaff: [],   // mutable ordered copy for drag-and-drop
      shiftTypes: [],
      seriesList: [],
      scheduleEntries: [],

      // Assign dialog state
      showAssignDialog: false,
      currentStaff: null,
      currentDate: '',
      currentShiftTypeId: null
    }
  },

  computed: {
    ...mapGetters(["permission", "dict", "user"]),
    // 日期范围（当月所有天）
    dates() {
      if (!this.condition.month) return []
      const [year, month] = this.condition.month.split('-').map(Number)
      const daysInMonth = new Date(year, month, 0).getDate()
      const weekdays = ['日', '一', '二', '三', '四', '五', '六']
      const result = []
      for (let d = 1; d <= daysInMonth; d++) {
        const dt = new Date(year, month - 1, d)
        const dow = dt.getDay()
        result.push({
          date: `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
          day: d,
          weekday: weekdays[dow],
          isWeekend: dow === 0 || dow === 6
        })
      }
      return result
    },

    // 过滤后的人员列表（在已排序的 orderedStaff 基础上过滤）
    displayStaff() {
      return this.orderedStaff.filter(s => {
        if (this.condition.orgName && s.orgName !== this.condition.orgName) return false
        return true
      })
    },


    // 排班索引：{ staffId_date: shiftTypeId }
    entryIndex() {
      const idx = {}
      this.scheduleEntries.forEach(e => {
        idx[`${e.staffId}_${e.date}`] = e.shiftTypeId
      })
      return idx
    },

    startDate() {
      return this.dates.length ? this.dates[0].date : ''
    },
    endDate() {
      return this.dates.length ? this.dates[this.dates.length - 1].date : ''
    }
  },

  mounted() {
    this.loadMeta().then(() => this.loadData())
  },

  methods: {
    // 加载静态基础数据（医院、员工类型、班次类型）
    async loadMeta() {
      const [sftRes, stsRes] = await Promise.all([
        api.ShiftType.query({ pageSize: 999 }),
        api.ShiftTypeSeries.query({ pageSize: 999 })
      ])
      this.hospitals = this.dict.StaffOrg
      this.staffTypes = this.dict.TitleType
      this.shiftTypes = sftRes.items
      this.seriesList = stsRes.items
      this.condition.title = this.staffTypes[0]?.code ?? ""
    },

    // 加载人员列表和排班记录，并应用已保存的行排序
    async loadData() {
      this.loading = true
      try {
        const [staffRes, schedRes] = await Promise.all([
          api.Staff.query(this.condition),
          api.Schedule.query({ startDate: this.startDate, endDate: this.endDate, pageSize: 9999 })
        ])
        this.entities = staffRes
        this.staffList = staffRes.items
        this.scheduleEntries = schedRes.items
        await this.loadOrder()
      } finally {
        this.loading = false
      }
    },

    // 从后端拉取排序并重排 orderedStaff
    async loadOrder() {
      const titleType = this.condition.title
      if (!titleType) {
        this.orderedStaff = [...this.staffList]
        return
      }
      try {
        const res = await api.StaffOrder.getOrder(titleType)
        const savedIds = res.staffIds || []
        if (savedIds.length === 0) {
          // 尚无排序记录，使用服务端默认顺序
          this.orderedStaff = this.staffList.filter(s => s.title === titleType)
          return
        }
        // 按保存的 ID 顺序重排，末尾追加不在保存列表中的新员工
        const idMap = new Map(this.staffList.map(s => [s.id, s]))
        const ordered = savedIds.map(id => idMap.get(id)).filter(Boolean)
        const savedSet = new Set(savedIds)
        const extras = this.staffList.filter(s => s.title === titleType && !savedSet.has(s.id))
        this.orderedStaff = [...ordered, ...extras]
      } catch (e) {
        // 获取排序失败时降级使用原始顺序
        this.orderedStaff = this.staffList.filter(s => s.title === titleType)
      }
    },

    // 拖拽结束后保存新顺序
    async onDragEnd() {
      const titleType = this.condition.title
      if (!titleType) return
      try {
        await api.StaffOrder.saveOrder({
          titleType,
          staffIds: this.orderedStaff.map(s => s.id)
        })
      } catch (e) {
        this.$message.error('排序保存失败')
      }
    },

    // 获取对应格子的班次名称
    getShiftName(staffId, date) {
      const shiftTypeId = this.entryIndex[`${staffId}_${date}`]
      if (!shiftTypeId) return ''
      const st = this.shiftTypes.find(s => s.id === shiftTypeId)
      return st ? st.name : ''
    },

    // 获取格子样式
    getCellStyle(staffId, date) {
      const shiftTypeId = this.entryIndex[`${staffId}_${date}`]
      if (!shiftTypeId) return {}
      const st = this.shiftTypes.find(s => s.id === shiftTypeId)
      return st ? { backgroundColor: st.color } : {}
    },

    // 计算指定人员当月工作量合计
    getStaffWorkload(staffId) {
      let total = 0
      this.dates.forEach(d => {
        const shiftTypeId = this.entryIndex[`${staffId}_${d.date}`]
        if (shiftTypeId) {
          const st = this.shiftTypes.find(s => s.id === shiftTypeId)
          if (st && st.workload !== undefined) {
            total += Number(st.workload)
          }
        }
      })
      return total
    },

    onCellClick(staff, date) {
      this.currentStaff = staff
      this.currentDate = date
      this.currentShiftTypeId = this.entryIndex[`${staff.id}_${date}`] || null
      this.showAssignDialog = true
    },

    getExistingEntryId(staffId, dateStr) {
      const entry = this.scheduleEntries.find(e => e.staffId === staffId && e.date === dateStr)
      return entry ? entry.id : null
    },

    // ===== 导出 Excel（每种员工类型一个 Sheet） =====
    async exportExcel() {
      this.exporting = true
      try {
        // 1. 全量拉取人员 & 排班（不分页）
        const [staffRes, schedRes] = await Promise.all([
          api.Staff.query({
            orgName: this.condition.orgName,
            deleted: false,
            pageSize: 99999
          }),
          api.Schedule.query({
            startDate: this.startDate,
            endDate: this.endDate,
            pageSize: 99999
          })
        ])
        const allStaff = staffRes.items
        const allEntries = schedRes.items


        // 2. 排班索引 staffId_date -> shiftTypeId
        const idx = {}
        allEntries.forEach(e => { idx[`${e.staffId}_${e.date}`] = e.shiftTypeId })

        // 3. 辅助：hex #rrggbb -> ARGB FFrrggbb
        const toArgb = hex => 'FF' + (hex || '#ffffff').replace('#', '').toUpperCase()

        // 4. 基础信息
        const [year, mon] = (this.condition.month || '').split('-')
        const FIXED_COLS = 3
        const totalCols = FIXED_COLS + this.dates.length + 1

        const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } }
        const noFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
        const headerFont = { bold: true, size: 10 }
        const centerAlign = { horizontal: 'center', vertical: 'middle' }
        const thinBorder = {
          top: { style: 'thin' }, bottom: { style: 'thin' },
          left: { style: 'thin' }, right: { style: 'thin' }
        }
        const applyHdr = cell => {
          cell.font = headerFont; cell.fill = headerFill
          cell.alignment = centerAlign; cell.border = thinBorder
        }

        const buildSheet = (wb, typeName, typeStaff) => {
          const ws = wb.addWorksheet(typeName)
          ws.columns = [
            { width: 10 }, { width: 10 }, { width: 14 },
            ...this.dates.map(() => ({ width: 6 })),
            { width: 10 }
          ]
          ws.addRow([`${year}年${mon}月份${typeName}排班表`])
          ws.mergeCells(1, 1, 1, totalCols)
          const tc = ws.getCell(1, 1)
          tc.font = { bold: true, size: 14 }; tc.alignment = centerAlign; tc.fill = noFill
          ws.getRow(1).height = 24

          const dhr = ws.addRow(['医院', '姓名', '电话',
            ...this.dates.map(d => `${Number(mon)}月${d.day}日`), '合计'])
          dhr.eachCell(cell => applyHdr(cell))
          dhr.height = 16

          const whr = ws.addRow(['', '', '', ...this.dates.map(d => d.weekday), ''])
          whr.eachCell((cell, colNum) => {
            cell.font = headerFont; cell.alignment = centerAlign; cell.border = thinBorder
            const di = colNum - FIXED_COLS - 1
            // cell.fill = (di >= 0 && di < this.dates.length && this.dates[di].isWeekend) ? headerFill : noFill
          })
          whr.height = 16

          for (const col of [1, 2, 3, totalCols]) {
            ws.mergeCells(2, col, 3, col)
            const cell = dhr.getCell(col)
            cell.fill = headerFill; cell.font = headerFont
            cell.alignment = centerAlign; cell.border = thinBorder
          }

          typeStaff.forEach(staff => {
            const orgLabel = (this.dict.StaffOrg || []).find(o => o.code === staff.orgName)
            const rowData = [
              orgLabel ? orgLabel.text : (staff.orgName || ''),
              staff.name, staff.phone || ''
            ]
            let workload = 0
            const colors = []
            this.dates.forEach(d => {
              const stId = idx[`${staff.id}_${d.date}`]
              const st = stId ? this.shiftTypes.find(s => s.id === stId) : null
              rowData.push(st ? st.name : '')
              colors.push(st ? st.color : null)
              if (st && st.workload !== undefined) workload += Number(st.workload)
            })
            rowData.push(workload || '')
            const dr = ws.addRow(rowData)
            dr.height = 18
            dr.eachCell((cell, colNum) => {
              cell.alignment = centerAlign; cell.border = thinBorder; cell.font = { size: 10 }
              const di = colNum - FIXED_COLS - 1
              if (di >= 0 && di < this.dates.length) {
                const color = colors[di]
                if (color) {
                  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(color) } }
                } else if (this.dates[di].isWeekend) {
                  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF9EC' } }
                }
              }
            })
          })
        }

        const wb = new ExcelJS.Workbook()
        this.staffTypes.forEach(type => {
          const typeStaff = allStaff.filter(s =>
            s.title === type.code || s.titleId === type.id || s.staffTypeId === type.id
          )

          if (typeStaff.length === 0) return
          buildSheet(wb, type.name, typeStaff)
        })

        if (wb.worksheets.length === 0) {
          this.$message.warning('没有可导出的数据，请查看浏览器控制台')
          return
        }



        // 6. 生成 Blob 并触发下载
        const buffer = await wb.xlsx.writeBuffer()
        const uint8 = new Uint8Array(buffer)
        let binary = ''
        for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i])
        const base64 = btoa(binary)
        const dataUrl = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64}`
        const fileName = `排班表_${this.condition.month || 'export'}.xlsx`
        const link = document.createElement('a')
        link.href = dataUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        this.$message.success(`导出成功，共 ${wb.worksheets.length} 个 Sheet`)
      } catch (err) {
        console.error(err)
        this.$message.error('导出失败: ' + err)
      } finally {
        this.exporting = false
      }
    },

    async handleCommand(command) {
      if (command === 'exportSubsidy') {
        this.exporting = true
        try {
          const [staffRes, schedRes] = await Promise.all([
            api.Staff.query({ orgName: this.condition.orgName, deleted: false, pageSize: 99999 }),
            api.Schedule.query({ startDate: this.startDate, endDate: this.endDate, pageSize: 99999 })
          ])
          const allStaff = staffRes.items
          const allEntries = schedRes.items

          const idx = {}
          allEntries.forEach(e => { idx[`${e.staffId}_${e.date}`] = e.shiftTypeId })

          const exportData = []

          allStaff.forEach(staff => {
            let workDays = 0
            let hasSchedule = false
            this.dates.forEach(d => {
              const stId = idx[`${staff.id}_${d.date}`]
              if (stId) {
                hasSchedule = true
                const st = this.shiftTypes.find(s => s.id === stId)
                if (st && st.workload !== undefined) {
                  workDays += Number(st.workload)
                }
              }
            })

            // 忽略没有配置排班的人员
            if (!hasSchedule) return

            const restDays = this.dates.length - workDays

            // 提取职务字典中的 extra 作为职务补贴
            const titleDict = (this.dict.TitleType || []).find(t => t.code === staff.title)
            let subsidyPerDay = 0
            if (titleDict && titleDict.extra) {
              const parsed = Number(titleDict.extra)
              if (!isNaN(parsed)) subsidyPerDay = parsed
            }
            const totalSubsidy = workDays * subsidyPerDay

            // 格式化科室、医院、职务
            const deptName = this.$utils.Dict.text(staff.deptCode, 'Dept') || staff.deptCode || ''
            const orgName = this.$utils.Dict.text(staff.orgName, 'StaffOrg') || staff.orgName || ''
            const titleName = titleDict ? titleDict.text : (staff.title || '')

            exportData.push({
              orgName,
              deptName,
              titleName,
              name: staff.name,
              phone: staff.phone || '',
              idNo: staff.idNo || staff.idCard || '',
              workDays,
              restDays,
              totalSubsidy
            })
          })

          if (exportData.length === 0) {
            this.$message.warning('没有可导出的补贴汇总数据')
            return
          }

          // 按 医院、科室、职务 排序
          exportData.sort((a, b) => {
            if (a.orgName !== b.orgName) return a.orgName.localeCompare(b.orgName)
            if (a.deptName !== b.deptName) return a.deptName.localeCompare(b.deptName)
            return a.titleName.localeCompare(b.titleName)
          })

          // 生成 Excel
          const wb = new ExcelJS.Workbook()
          const ws = wb.addWorksheet('补贴汇总')
          ws.columns = [
            { width: 15 }, { width: 20 }, { width: 12 },
            { width: 10 }, { width: 15 }, { width: 20 },
            { width: 12 }, { width: 12 }, { width: 12 }
          ]

          const [year, mon] = (this.condition.month || '').split('-')
          ws.addRow([`${year}年${Number(mon)}月份补贴汇总`])
          ws.mergeCells(1, 1, 1, 9)
          const titleCell = ws.getCell(1, 1)
          titleCell.font = { bold: true, size: 16 }
          titleCell.alignment = { horizontal: 'center', vertical: 'middle' }
          ws.getRow(1).height = 30

          const headers = ['医院', '科室', '职务', '姓名', '手机号', '身份证号码', '上班天数', '休息天数', '补贴金额']
          const headerRow = ws.addRow(headers)
          headerRow.height = 20
          headerRow.eachCell(cell => {
            cell.font = { bold: true, size: 11 }
            cell.alignment = { horizontal: 'center', vertical: 'middle' }
            cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }
          })

          exportData.forEach(row => {
            const dataRow = ws.addRow([
              row.orgName, row.deptName, row.titleName,
              row.name, row.phone, row.idNo,
              row.workDays, row.restDays, row.totalSubsidy
            ])
            dataRow.height = 18
            dataRow.eachCell(cell => {
              cell.alignment = { horizontal: 'center', vertical: 'middle' }
              cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }
              cell.font = { size: 11 }
            })
          })

          const buffer = await wb.xlsx.writeBuffer()
          const uint8 = new Uint8Array(buffer)
          let binary = ''
          for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i])
          const base64 = btoa(binary)
          const dataUrl = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64}`

          const link = document.createElement('a')
          link.href = dataUrl
          link.download = `补贴汇总_${this.condition.month || 'export'}.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          this.$message.success('补贴汇总导出成功')
        } catch (err) {
          console.error(err)
          this.$message.error('导出失败: ' + err)
        } finally {
          this.exporting = false
        }
      }
    },

    async handleAssignOk(data) {
      this.loading = true
      const staffId = this.currentStaff.id
      const startDate = new Date(data.date)

      try {
        if (data.assignType === 'single') {
          // 调整一天
          if (!data.shiftTypeId) {
            this.loading = false
            return
          }
          const id = this.getExistingEntryId(staffId, data.date)
          await api.Schedule.save({
            id: id,
            staffId: staffId,
            date: data.date,
            shiftTypeId: data.shiftTypeId
          })
          this.$message.success('已排班')
        } else if (data.assignType === 'series') {
          // 调整系列
          if (!data.seriesId) {
            this.loading = false
            return
          }

          const series = this.seriesList.find(s => s.id === data.seriesId)
          if (!series || !series.items || series.items.length === 0) {
            this.$message.warning('该班次系列无可用班次')
            this.loading = false
            return
          }

          // 计算结束日期: 当前月份 + durationMonths
          const endDate = new Date(startDate)
          endDate.setMonth(endDate.getMonth() + Number(data.durationMonths))

          let offset = data.seriesOffset || 0

          const utc1 = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          const utc2 = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
          const days = Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));

          if (days <= 0) {
            this.$message.warning('排班范围无效')
            this.loading = false
            return
          }

          const dateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`

          await api.Schedule.batchAdd({
            staffId: staffId,
            startDate: dateStr,
            shiftTypeSeriesId: data.seriesId,
            startShiftType: offset,
            days: days
          })

          this.$message.success(`已连续排班 ${days} 天`)
        }

        // 重新加载数据以刷新视图
        await this.loadData()
      } catch (err) {
        this.$message.error('排班保存失败: ' + err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* ===== 图例 ===== */
.legend-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  padding: 6px 12px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
  font-size: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #606266;
}

.legend-dot {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

/* ===== 表格容器 ===== */
.schedule-wrap {
  flex: 1;
  overflow: auto;
  height: calc(100% - 38px);
}

.schedule-table {
  border-collapse: collapse;
  white-space: nowrap;
  font-size: 12px;
  width: max-content;
  min-width: 100%;
}

/* ===== 表头 ===== */
.header-row th {
  position: sticky;
  top: 0;
  background: #fafafa;
  border: 1px solid #e4e7ed;
  padding: 4px 6px;
  text-align: center;
  z-index: 10;
  font-weight: 600;
  color: #303133;
}

.header-row .sticky-col {
  z-index: 20;
}

/* ===== 周末列高亮 ===== */
.date-col.weekend {
  background: #fef9ec;
  color: #e6a23c;
}

/* ===== 粘性固定列 ===== */
.sticky-col {
  position: sticky;
  background: #fff;
  border: 1px solid #e4e7ed;
}

.col-drag {
  left: 0;
  width: 28px;
  min-width: 28px;
  z-index: 5;
  text-align: center;
  color: #c0c4cc;
}

.drag-handle {
  cursor: grab;
  font-size: 14px;
  padding: 2px 4px;
}

.drag-handle:active {
  cursor: grabbing;
}

.col-hospital {
  left: 28px;
  min-width: 52px;
  z-index: 5;
  text-align: center;
}

.col-name {
  left: 80px;
  min-width: 64px;
  z-index: 5;
}

.col-phone {
  left: 144px;
  min-width: 100px;
  z-index: 5;
}

.col-total {
  right: 0;
  min-width: 80px;
  z-index: 5;
  background: #fafafa !important;
  font-weight: bold;
  color: #409EFF;
}

/* ===== 数据行 ===== */
.staff-row td {
  border: 1px solid #e4e7ed;
  padding: 3px 5px;
  text-align: center;
  height: 28px;
  line-height: 22px;
}

.staff-row:hover td {
  background-color: #ecf5ff !important;
}

/* ===== 班次格子 ===== */
.shift-cell {
  min-width: 36px;
  cursor: pointer;
  transition: filter 0.15s;
  font-size: 11px;
}

.shift-cell:hover {
  filter: brightness(0.9);
}

/* ===== 日期列 ===== */
.date-col {
  min-width: 36px;
  text-align: center;
  font-size: 11px;
}

.date-col .weekday {
  font-size: 10px;
  color: #909399;
}
</style>
